import React, { useState, useCallback } from "react"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import {
  useGetAdminPendingApprovalsQuery,
  useApproveSimulationMutation,
  useRejectSimulationMutation,
  useCreateTestSessionMutation,
  useGetTestSessionsForSimulationQuery,
  PendingApprovalSimulation,
} from "../../app/services/meseeknaApiV2"
import { MdOutlineApproval, MdCheck, MdClose, MdInfo, MdScience, MdContentCopy, MdCheckCircle } from "react-icons/md"
import { FiClock, FiUser, FiX, FiChevronDown, FiChevronUp } from "react-icons/fi"
import { HiOutlineOfficeBuilding } from "react-icons/hi"
import { BiTestTube } from "react-icons/bi"

// Icon wrapper components for TypeScript compatibility
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ApprovalIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineApproval {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CheckIcon = (props: { size?: number; className?: string }): JSX.Element => <MdCheck {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CloseIcon = (props: { size?: number; className?: string }): JSX.Element => <MdClose {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const InfoIcon = (props: { size?: number; className?: string }): JSX.Element => <MdInfo {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ClockIcon = (props: { size?: number; className?: string }): JSX.Element => <FiClock {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UserIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUser {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const BuildingIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineOfficeBuilding {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const TestIcon = (props: { size?: number; className?: string }): JSX.Element => <MdScience {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CopyIcon = (props: { size?: number; className?: string }): JSX.Element => <MdContentCopy {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CheckCircleIcon = (props: { size?: number; className?: string }): JSX.Element => <MdCheckCircle {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const XIcon = (props: { size?: number; className?: string }): JSX.Element => <FiX {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronDownIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronDown {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronUpIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronUp {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const TestTubeIcon = (props: { size?: number; className?: string }): JSX.Element => <BiTestTube {...props} />

// Quick Test Results Component
const QuickTestResultsSection: React.FC<{
  simulationId: string
}> = ({ simulationId }) => {
  const { data, isLoading } = useGetTestSessionsForSimulationQuery(simulationId)
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set())
  const [showAllSessions, setShowAllSessions] = useState(false)

  const toggleRunExpansion = (runId: string) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev)
      if (next.has(runId)) {
        next.delete(runId)
      } else {
        next.add(runId)
      }
      return next
    })
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  if (isLoading) {
    return (
      <div className="mt-4 p-3 bg-gray-50 rounded-lg animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
      </div>
    )
  }

  const sessions = data?.data || []
  const completedRuns = sessions.flatMap((s) => s.completedRuns)

  if (sessions.length === 0) {
    return null
  }

  const displaySessions = showAllSessions ? sessions : sessions.slice(0, 2)

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TestTubeIcon size={16} className="text-purple-600" />
          <h4 className="text-sm font-semibold text-gray-900">Quick Test Results</h4>
          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
            {completedRuns.length} completed
          </span>
        </div>
      </div>

      <div className="space-y-3">
        {displaySessions.map((session) => (
          <div key={session.id} className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-800">{session.name}</span>
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                session.status === "active" ? "bg-green-100 text-green-700" :
                session.status === "completed" ? "bg-blue-100 text-blue-700" :
                session.status === "expired" ? "bg-gray-100 text-gray-700" :
                "bg-yellow-100 text-yellow-700"
              }`}>
                {session.status}
              </span>
            </div>

            {session.completedRuns.length > 0 ? (
              <div className="space-y-2">
                {session.completedRuns.map((run) => (
                  <div key={run.id} className="bg-white rounded border border-gray-200">
                    <button
                      onClick={() => toggleRunExpansion(run.id)}
                      className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Run #{run.runNumber}</span>
                        <span className="text-sm text-gray-700">
                          {run.participantName || run.participantEmail}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatDuration(run.elapsedSeconds)}
                        </span>
                      </div>
                      {expandedRuns.has(run.id) ? (
                        <ChevronUpIcon size={16} className="text-gray-400" />
                      ) : (
                        <ChevronDownIcon size={16} className="text-gray-400" />
                      )}
                    </button>

                    {expandedRuns.has(run.id) && run.finalResultData && (
                      <div className="px-3 pb-3 border-t border-gray-100">
                        <div className="mt-2 text-xs text-gray-500 mb-1">
                          Completed: {formatDate(run.completedAt)}
                        </div>
                        <details className="mt-2">
                          <summary className="text-xs text-blue-600 cursor-pointer hover:underline">
                            View Result JSON
                          </summary>
                          <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-48">
                            {JSON.stringify(run.finalResultData, null, 2)}
                          </pre>
                        </details>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500">No completed runs yet</p>
            )}
          </div>
        ))}
      </div>

      {sessions.length > 2 && (
        <button
          onClick={() => setShowAllSessions(!showAllSessions)}
          className="mt-2 text-xs text-blue-600 hover:underline"
        >
          {showAllSessions ? "Show less" : `Show ${sessions.length - 2} more sessions`}
        </button>
      )}
    </div>
  )
}

// Quick Test Modal Component
const QuickTestModal: React.FC<{
  simulation: PendingApprovalSimulation
  onClose: () => void
}> = ({ simulation, onClose }) => {
  const [sessionName, setSessionName] = useState(`Test: ${simulation.name}`)
  const [testerEmails, setTesterEmails] = useState("")
  const [isCreating, setIsCreating] = useState(false)
  const [result, setResult] = useState<{
    passcode: string
    shareableLink: string
    expiresAt: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const [createTestSession] = useCreateTestSessionMutation()

  const handleCreate = useCallback(async () => {
    setIsCreating(true)
    setError(null)

    try {
      const emailList = testerEmails
        .split(/[,\n]/)
        .map(e => e.trim())
        .filter(e => e.length > 0)

      const response = await createTestSession({
        name: sessionName,
        simulationId: simulation.id,
        simulationType: "V2_SIMULATION",
        testerEmails: emailList.length > 0 ? emailList : undefined,
      }).unwrap()

      setResult({
        passcode: response.passcode,
        shareableLink: response.shareableLink,
        expiresAt: response.expiresAt,
      })
    } catch (err: unknown) {
      console.error("Failed to create test session:", err)
      setError((err as { data?: { message?: string } })?.data?.message || "Failed to create test session")
    } finally {
      setIsCreating(false)
    }
  }, [sessionName, simulation.id, testerEmails, createTestSession])

  const copyToClipboard = useCallback((text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }, [])

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <TestIcon size={24} className="text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Quick Test Session</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XIcon size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {!result ? (
            <>
              {/* Simulation Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Simulation</p>
                <p className="font-medium text-gray-900">{simulation.name}</p>
              </div>

              {/* Session Name */}
              <div className="mb-4">
                <label htmlFor="sessionName" className="block text-sm font-medium text-gray-700 mb-1">
                  Test Session Name
                </label>
                <input
                  type="text"
                  id="sessionName"
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter session name"
                />
              </div>

              {/* Tester Emails (Optional) */}
              <div className="mb-4">
                <label htmlFor="testerEmails" className="block text-sm font-medium text-gray-700 mb-1">
                  Tester Emails (Optional)
                </label>
                <textarea
                  id="testerEmails"
                  value={testerEmails}
                  onChange={(e) => setTesterEmails(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter emails separated by commas or newlines"
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add emails of testers who should have access
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={isCreating || !sessionName.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isCreating ? "Creating..." : "Create Test Session"}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Success State */}
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircleIcon size={24} className="text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Test Session Created!</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Share the link and passcode with your QA team
                </p>
              </div>

              {/* Passcode */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Passcode</label>
                <div className="flex gap-2">
                  <div className="flex-1 px-4 py-3 bg-gray-50 rounded-lg font-mono text-lg tracking-widest text-center">
                    {result.passcode}
                  </div>
                  <button
                    onClick={() => copyToClipboard(result.passcode, "passcode")}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {copiedField === "passcode" ? (
                      <CheckCircleIcon size={20} className="text-green-600" />
                    ) : (
                      <CopyIcon size={20} className="text-gray-500" />
                    )}
                  </button>
                </div>
              </div>

              {/* Shareable Link */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Shareable Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={result.shareableLink}
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600"
                  />
                  <button
                    onClick={() => copyToClipboard(result.shareableLink, "link")}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {copiedField === "link" ? (
                      <CheckCircleIcon size={20} className="text-green-600" />
                    ) : (
                      <CopyIcon size={20} className="text-gray-500" />
                    )}
                  </button>
                </div>
              </div>

              {/* Expiration */}
              <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-700">
                  <ClockIcon size={14} className="inline mr-1" />
                  Session expires on {formatDate(result.expiresAt)}
                </p>
              </div>

              {/* Copy All Button */}
              <button
                onClick={() => copyToClipboard(
                  `Test Session Link: ${result.shareableLink}\nPasscode: ${result.passcode}`,
                  "all"
                )}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors mb-3"
              >
                {copiedField === "all" ? "Copied!" : "Copy Link & Passcode"}
              </button>

              <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const ApprovalCard: React.FC<{
  simulation: PendingApprovalSimulation
  onApprove: (id: string) => void
  onReject: (id: string) => void
  onQuickTest: (simulation: PendingApprovalSimulation) => void
  isApproving: boolean
  isRejecting: boolean
}> = ({ simulation, onApprove, onReject, onQuickTest, isApproving, isRejecting }) => {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toUpperCase()) {
      case "BEGINNER":
        return "bg-green-100 text-green-800"
      case "INTERMEDIATE":
        return "bg-yellow-100 text-yellow-800"
      case "ADVANCED":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{simulation.name}</h3>
          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
            {simulation.description || "No description provided"}
          </p>
        </div>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(simulation.difficulty)}`}
        >
          {simulation.difficulty}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div className="flex items-center gap-2 text-gray-600">
          <UserIcon size={14} />
          <span>{simulation.creatorName || simulation.creatorEmail}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-600">
          <BuildingIcon size={14} />
          <span>{simulation.organizationName || "No organization"}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-600">
          <ClockIcon size={14} />
          <span>{simulation.estimatedDuration ? `${simulation.estimatedDuration} min` : "N/A"}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-600">
          <InfoIcon size={14} />
          <span>{simulation.industry || "General"}</span>
        </div>
      </div>

      <div className="text-xs text-gray-400 mb-4">
        Created: {formatDate(simulation.createdAt)} | Updated: {formatDate(simulation.updatedAt)}
      </div>

      {/* Quick Test Button */}
      <div className="mb-3">
        <button
          onClick={() => onQuickTest(simulation)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <TestIcon size={18} />
          Quick Test
        </button>
      </div>

      {/* Quick Test Results Section */}
      <QuickTestResultsSection simulationId={simulation.id} />

      <div className="flex gap-3 mt-4">
        <button
          onClick={() => onApprove(simulation.id)}
          disabled={isApproving || isRejecting}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <CheckIcon size={18} />
          {isApproving ? "Approving..." : "Approve & Publish"}
        </button>
        <button
          onClick={() => onReject(simulation.id)}
          disabled={isApproving || isRejecting}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <CloseIcon size={18} />
          {isRejecting ? "Rejecting..." : "Reject"}
        </button>
      </div>
    </div>
  )
}

const PendingApprovalsPage: React.FC = () => {
  const { data, isLoading, refetch } = useGetAdminPendingApprovalsQuery()
  const [approveSimulation, { isLoading: isApproving }] = useApproveSimulationMutation()
  const [rejectSimulation, { isLoading: isRejecting }] = useRejectSimulationMutation()
  const [actioningId, setActioningId] = useState<string | null>(null)
  const [selectedSimulation, setSelectedSimulation] = useState<PendingApprovalSimulation | null>(null)
  const [showQuickTestModal, setShowQuickTestModal] = useState(false)

  const pendingApprovals = data?.data || []

  const handleQuickTest = useCallback((simulation: PendingApprovalSimulation) => {
    setSelectedSimulation(simulation)
    setShowQuickTestModal(true)
  }, [])

  const handleCloseQuickTestModal = useCallback(() => {
    setShowQuickTestModal(false)
    setSelectedSimulation(null)
  }, [])

  const handleApprove = async (id: string) => {
    setActioningId(id)
    try {
      await approveSimulation(id).unwrap()
      refetch()
    } catch (error) {
      console.error("Failed to approve simulation:", error)
    } finally {
      setActioningId(null)
    }
  }

  const handleReject = async (id: string) => {
    setActioningId(id)
    try {
      await rejectSimulation(id).unwrap()
      refetch()
    } catch (error) {
      console.error("Failed to reject simulation:", error)
    } finally {
      setActioningId(null)
    }
  }

  return (
    <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <ApprovalIcon size={24} className="text-amber-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Pending Approvals
                  </h1>
                  <p className="text-gray-500">
                    Review and approve custom simulations
                  </p>
                </div>
              </div>
            </div>

            {/* Stats Banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <ApprovalIcon size={20} className="text-amber-600" />
                <span className="font-medium text-amber-800">
                  {pendingApprovals.length} simulation{pendingApprovals.length !== 1 ? "s" : ""} pending review
                </span>
              </div>
            </div>

            {/* Content */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse"
                  >
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </div>
                    <div className="flex gap-3">
                      <div className="h-10 bg-gray-200 rounded flex-1"></div>
                      <div className="h-10 bg-gray-200 rounded flex-1"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : pendingApprovals.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {pendingApprovals.map((simulation) => (
                  <ApprovalCard
                    key={simulation.id}
                    simulation={simulation}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onQuickTest={handleQuickTest}
                    isApproving={isApproving && actioningId === simulation.id}
                    isRejecting={isRejecting && actioningId === simulation.id}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <ApprovalIcon size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Pending Approvals
                </h3>
                <p className="text-gray-500">
                  All custom simulations have been reviewed. Check back later for new submissions.
                </p>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Quick Test Modal */}
      {showQuickTestModal && selectedSimulation && (
        <QuickTestModal
          simulation={selectedSimulation}
          onClose={handleCloseQuickTestModal}
        />
      )}
    </div>
  )
}

export default PendingApprovalsPage
